import {
  BaseUser,
  EntitlementType,
  OrgScopedBaseUser,
  User,
} from "src/stores/models/user";
import {
  QualityEventAnswers,
  QualityEventDraft,
  QualityEventField,
  QualityEventProposal,
  QualityEventRecord,
  QualityEventStep,
  isQualityEventApproval,
  qualityEventRows,
} from "src/types/quality-events";
import { hasFunctionalGroupEntitlement } from "src/utils/entitlements";

const proposalAnswers = (
  fields: QualityEventField[],
  answers: QualityEventProposal["answers"],
  users: BaseUser[],
  previous: QualityEventAnswers
): QualityEventAnswers => {
  const result: QualityEventAnswers = {};
  const userIds = new Set(users.map((user) => user.id));
  for (const field of fields) {
    const value = answers[field.key];
    if (value === undefined) continue;
    if (field.type === "user") {
      if (value === null || (typeof value === "string" && userIds.has(value)))
        result[field.key] = value;
    } else if (field.type === "approval") {
      if (value && typeof value === "object" && "approver" in value) {
        if (value.approver === null || userIds.has(value.approver))
          result[field.key] = {
            approver: value.approver,
            dueDate: value.dueDate ?? null,
          };
      } else result[field.key] = null;
    } else if (field.type === "repeatable") {
      // A value for another field type must not erase existing rows.
      if (value === null) result[field.key] = [];
      else if (
        Array.isArray(value) &&
        value.every((row) => typeof row === "object")
      ) {
        const previousRows = qualityEventRows(previous[field.key]);
        result[field.key] = value.map((row) => {
          const nextRow: { id: string; answers: QualityEventAnswers } = {
            id: row.id ?? crypto.randomUUID(),
            answers: { ...row.answers },
          };
          const previousRow = previousRows.find((item) => item.id === row.id);
          for (const [key, answer] of Object.entries(row.answers)) {
            const child = field.fields.find((child) => child.key === key);
            if (
              child?.type !== "user" ||
              answer === null ||
              users.some((user) => user.id === answer)
            )
              continue;
            // Unknown suggested users must not erase a saved assignment.
            const previousAnswer = previousRow?.answers[key];
            if (previousAnswer === undefined) delete nextRow.answers[key];
            else nextRow.answers[key] = previousAnswer;
          }
          return nextRow;
        });
      }
    } else if (
      value === null ||
      typeof value !== "object" ||
      (Array.isArray(value) && value.every((item) => typeof item === "string"))
    )
      result[field.key] = value;
  }
  return result;
};

export const getQualityEventDraft = ({
  step,
  record,
  proposal,
  users = [],
  creatorId = record?.createdBy.id,
}: {
  step: QualityEventStep;
  record?: QualityEventRecord;
  proposal?: QualityEventProposal;
  users?: BaseUser[];
  creatorId?: string;
}): QualityEventDraft => {
  const existingStep = record?.steps[step.key];
  let owner = existingStep ? existingStep.owner : null;
  let dueDate = existingStep?.dueDate ?? null;
  const proposedOwner = users.find((user) => user.id === proposal?.owner);
  if (
    proposal?.owner === null ||
    (creatorId !== undefined && proposal?.owner === creatorId)
  ) {
    owner = null;
    if (proposal?.dueDate !== undefined) dueDate = proposal.dueDate;
  } else if (proposal?.owner === undefined || proposedOwner) {
    // An unknown user must not overwrite the existing owner or deadline.
    owner = proposedOwner?.id ?? owner;
    if (proposal?.dueDate !== undefined) dueDate = proposal.dueDate;
  }
  const answers = { ...existingStep?.answers };
  for (const field of step.fields) {
    const value = answers[field.key];
    if (field.type === "approval" && isQualityEventApproval(value))
      answers[field.key] = { approver: value.approver, dueDate: value.dueDate };
  }
  return {
    id: record?.id ?? proposal?.id ?? crypto.randomUUID(),
    expectedVersion: proposal?.expectedVersion ?? record?.version,
    title: proposal?.title ?? record?.title ?? "",
    owner,
    dueDate,
    answers: {
      ...answers,
      ...proposalAnswers(
        step.fields.filter((field) => record || field.type !== "approval"),
        proposal?.answers ?? {},
        users,
        answers
      ),
    },
    files: {},
  };
};
export const isQualityEventStepUnlocked = (
  record: QualityEventRecord,
  stepKey: string
) => {
  const steps = record.definition.definition.steps;
  const index = steps.findIndex((step) => step.key === stepKey);
  const currentIndex = steps.findIndex(
    (step) => step.key === record.currentStepKey
  );
  return record.status !== "CLOSED" && index >= 0 && index <= currentIndex;
};

export const canEditQualityEventRecord = (
  record: QualityEventRecord,
  user:
    | (Pick<User, "id" | "permissions"> &
        Pick<OrgScopedBaseUser, "organizationMemberships">)
    | undefined
) => {
  if (record.status === "CLOSED" || !user) return false;
  const editing = record.definition.definition.editing;
  if (!editing) return true;
  return !!(
    (editing.recordOwner && record.createdBy.id === user.id) ||
    (editing.stepResponsible &&
      record.definition.definition.steps.some(
        (step) =>
          (record.steps[step.key].owner ?? record.createdBy.id) === user.id ||
          step.fields.some((field) => {
            if (field.type !== "repeatable" || !field.task) return false;
            const { assigneeField } = field.task;
            return qualityEventRows(
              record.steps[step.key].answers[field.key]
            ).some((row) => row.answers[assigneeField] === user.id);
          })
      )) ||
    user.permissions?.includes(editing.adminPermission) ||
    user.organizationMemberships?.some(
      (membership) =>
        membership.organizationId === record.organizationId &&
        membership.activated &&
        hasFunctionalGroupEntitlement(
          user,
          record.organizationId,
          EntitlementType.ADMINISTER_ALL_TRAININGS
        )
    )
  );
};

export const prepareQualityEventChanges = async (
  drafts: Record<string, QualityEventDraft>,
  stepKey: string,
  firstStepKey: string,
  prepare: (draft: QualityEventDraft) => Promise<{
    title: string;
    owner?: string | null;
    dueDate?: string | null;
    answers: QualityEventAnswers;
  }>
) => {
  const prepared = await Promise.all(
    Object.entries(drafts).map(
      async ([key, draft]) => [key, await prepare(draft)] as const
    )
  );
  const values = Object.fromEntries(prepared);
  const steps = Object.fromEntries(
    prepared
      .filter(([key]) => key !== stepKey)
      .map(([key, { title: _title, ...fields }]) => [key, fields])
  );
  return {
    ...values[stepKey],
    title: drafts[firstStepKey]?.title ?? drafts[stepKey].title,
    ...(Object.keys(steps).length > 0 && { steps }),
  };
};
